import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { scrollToSection, EASE } from "./Reveal";

export default function CaseStudy({ study, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis && window.__lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis && window.__lenis.start();
    };
  }, [onClose]);

  const s = study;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[90] bg-white overflow-y-auto"
      data-testid={`case-study-view-${s.brand.replace(/[^a-z]/gi, "").toLowerCase()}`}
    >
      <div className="sticky top-0 z-10 glass-nav">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1d1d1f] truncate">
            {s.brand} <span className="text-[#86868b] font-normal">— case study</span>
          </p>
          <button
            onClick={onClose}
            data-testid="case-study-close-btn"
            aria-label="Close case study"
            className="p-2 rounded-full hover:bg-black/5 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="max-w-5xl mx-auto px-4 sm:px-8 pt-10 pb-24"
      >
        <div className="spotlight-frame">
          <img src={s.img} alt={`${s.brand} — ${s.title}`} className="w-full h-[300px] sm:h-[440px] object-cover" />
        </div>

        <div className="mt-10 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#0066cc]">{s.brand}</p>
          <h1 className="mt-3 text-3xl sm:text-5xl font-semibold tracking-tight text-[#1d1d1f] leading-[1.08]">{s.title}</h1>
          <p className="mt-3 text-sm text-[#86868b]">{s.meta}</p>

          <div className="mt-10 flex flex-col gap-8">
            {[
              ["The brief", s.story.challenge],
              ["Her approach", s.story.approach],
              ["The outcome", s.story.outcome],
            ].map(([h, p]) => (
              <div key={h} className="border-l-2 border-[#d4af37]/60 pl-6">
                <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-[#86868b]">{h}</h2>
                <p className="mt-2.5 text-lg text-[#1d1d1f]/85 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>

          {s.gallery && s.gallery.length > 0 && (
            <div className="mt-12 grid sm:grid-cols-3 gap-4">
              {s.gallery.map((g, i) => (
                <motion.img
                  key={g}
                  src={g}
                  alt={`${s.brand} process ${i + 1}`}
                  loading="lazy"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                  className={`w-full rounded-2xl object-cover ${i === 0 ? "sm:col-span-2 sm:row-span-2 h-72 sm:h-[420px]" : "h-40 sm:h-[200px]"}`}
                />
              ))}
            </div>
          )}

          {s.services && (
            <div className="mt-12 flex flex-wrap gap-2">
              {s.services.map((sv) => (
                <span key={sv} className="px-4 py-2 rounded-full bg-[#f5f5f7] border border-[#e5e5e7] text-sm text-[#1d1d1f]/80">
                  {sv}
                </span>
              ))}
            </div>
          )}

          <div className="mt-14 rounded-[28px] bg-[#161617] text-white p-8 sm:p-12 text-center">
            <p className="font-serif-editorial italic text-2xl sm:text-3xl leading-snug">
              "Make the brand vision a reality."
            </p>
            <button
              data-testid="case-study-cta"
              onClick={() => { onClose(); setTimeout(() => scrollToSection("#booking"), 150); }}
              className="mt-7 px-7 py-3.5 rounded-full bg-white text-[#1d1d1f] text-sm font-medium hover:bg-[#e8e8ed] transition-all active:scale-[0.98] inline-flex items-center gap-2"
            >
              Discuss a similar brief <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
